import { HttpHandlerFn, HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';

import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, throwError, switchMap, from, BehaviorSubject, filter, take } from 'rxjs';

import { AuthService } from '../services/auth.service';

/**
 * Endpoints de autenticação que não devem disparar refresh/logout automático.
 */
const AUTH_EXEMPT_URLS = new Set([
  '/auth/me',
  '/auth/login',
  '/auth/register',
  '/auth/register-public',
  '/auth/refresh',
  '/auth/logout',
]);

let isRefreshing = false;

type RefreshState = { success: true } | { success: false; error: unknown } | null;

/**
 * Estado compartilhado entre requisições enquanto um refresh de sessão está em andamento.
 */
const refreshSubject = new BehaviorSubject<RefreshState>(null);

const isRefreshResolved = (value: RefreshState): value is Exclude<RefreshState, null> => value !== null;

const rethrowError = (error: unknown) => throwError(() => error);

function normalizeRequestPath(url: string): string {
  const path = url
    .split('?')[0]
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/api(?=\/)/i, '')
    .toLowerCase();

  return path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Aguarda o término do refresh em andamento e repete a request original quando houver sucesso.
 */
function waitForRefreshAndRetry(authReq: HttpRequest<unknown>, next: HttpHandlerFn) {
  return refreshSubject.pipe(
    filter(isRefreshResolved),
    take(1),
    switchMap((refreshState) => (refreshState.success ? next(authReq) : rethrowError(refreshState.error))),
  );
}

/**
 * Libera as requisições pendentes após refresh bem-sucedido e repete a chamada original.
 */
function retryRequest(authReq: HttpRequest<unknown>, next: HttpHandlerFn) {
  isRefreshing = false;
  refreshSubject.next({ success: true });

  return next(authReq);
}

/**
 * Executa logout quando o refresh falha e propaga o erro.
 */
function logoutAndRethrow(authService: AuthService, refreshError: unknown) {
  isRefreshing = false;

  return from(authService.logout()).pipe(
    catchError(() => from(Promise.resolve())),
    switchMap(() => {
      refreshSubject.next({
        success: false,
        error: refreshError,
      });

      return rethrowError(refreshError);
    }),
  );
}

/**
 * Tenta renovar a sessão atual e, em caso de sucesso, reenvia a request interceptada.
 */
function refreshSessionAndRetry(authService: AuthService, authReq: HttpRequest<unknown>, next: HttpHandlerFn) {
  isRefreshing = true;
  refreshSubject.next(null);

  return from(authService.refresh()).pipe(
    switchMap(() => retryRequest(authReq, next)),
    catchError((refreshError) => logoutAndRethrow(authService, refreshError)),
  );
}

/**
 * Centraliza o tratamento de respostas 401.
 */
function handleUnauthorizedError(
  error: HttpErrorResponse,
  isExempt: boolean,
  authService: AuthService,
  authReq: HttpRequest<unknown>,
  next: HttpHandlerFn,
) {
  if (error.status !== 401 || isExempt) {
    return rethrowError(error);
  }

  if (isRefreshing) {
    return waitForRefreshAndRetry(authReq, next);
  }

  return refreshSessionAndRetry(authService, authReq, next);
}

/**
 * Em 403 dentro de uma área administrativa, redireciona para uma tela segura (/projects).
 * Fora de /admin o erro é propagado, para não quebrar fluxos que tratam 403 na própria tela
 * (ex.: negociações de proposta/cota).
 */
function handleForbiddenError(error: HttpErrorResponse, router: Router) {
  if (error.status === 403 && router.url.startsWith('/admin')) {
    router.navigate(['/projects']);
  }

  return rethrowError(error);
}

/**
 * Interceptor responsável por enviar cookies de sessão e tentar renovar autenticação expirada.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const authReq = req.clone({
    withCredentials: true,
  });

  const requestPath = normalizeRequestPath(req.url);
  const isExempt = AUTH_EXEMPT_URLS.has(requestPath);

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        return handleForbiddenError(error, router);
      }

      return handleUnauthorizedError(error, isExempt, authService, authReq, next);
    }),
  );
};
